import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import { LoadingSpinner } from '../components/LoadingSpinner';

export function LayoutSequenceEditor({ onClose }) {
    const [layouts, setLayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    useEffect(() => {
        fetchLayouts();
    }, []);

    const fetchLayouts = async () => {
        try {
            const res = await apiClient.post('/minusone-service/master/layout', null);
            const data = res.data?.responseObject || [];
            setLayouts(data.sort((a, b) => a.orderSequence - b.orderSequence));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    // Smart Title with fallback
    const getDisplayTitle = (item) => {
        if (item.title && item.title.trim() !== '') {
            return item.title;
        }
        if (item.containerTypeDescription && item.containerTypeDescription.trim() !== '') {
            return item.containerTypeDescription;
        }
        return `Container #${item.id}`;
    };

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        setTimeout(() => e.target.style.opacity = '0.5', 0);
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex !== index) setDragOverIndex(index);
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) return;

        const newLayouts = [...layouts];
        const [movedItem] = newLayouts.splice(draggedIndex, 1);
        newLayouts.splice(dropIndex, 0, movedItem);

        setLayouts(newLayouts);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const saveSequence = async () => {
        setSaving(true);
        try {
            const payload = layouts.map((item, idx) => ({
                id: item.id,
                orderSequence: idx + 1
            }));

            console.log(payload);
            await apiClient.post('/minusone-service/layout/updateLayoutSequence', payload);
            alert('✅ Sequence Updated Successfully!');
            onClose();
        } catch (e) {
            alert('❌ Failed to save');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={{ padding: 60 }}><LoadingSpinner /></div>;

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, backdropFilter: 'blur(8px)'
        }}>
            <div style={{
                background: '#fff', width: 560, borderRadius: 20,
                boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{ padding: '24px 28px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h2 style={{ margin: 0, color: '#111' }}>Reorder Layout Sequence</h2>
                        <p style={{ margin: 4, color: '#666', fontSize: 14 }}>Drag to change order</p>
                    </div>
                    <button onClick={onClose} style={{ fontSize: 26, color: '#555', background: 'none', border: 'none' }}>✕</button>
                </div>

                {/* List */}
                <div style={{ padding: '20px', maxHeight: '65vh', overflowY: 'auto' }}>
                    {layouts.map((item, index) => (
                        <div
                            key={item.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnd={handleDragEnd}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            style={{
                                padding: '18px 22px',
                                marginBottom: 12,
                                background: draggedIndex === index ? '#f0e6ff' : '#fff',
                                border: dragOverIndex === index
                                    ? '2px dashed #6C3FC5'
                                    : '1px solid #ddd',
                                borderRadius: 16,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 16,
                                cursor: 'grab',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                            }}
                        >
                            <div style={{
                                width: 42, height: 42, background: '#6C3FC5',
                                color: '#fff', borderRadius: '50%', display: 'flex',
                                alignItems: 'center', justifyContent: 'center',
                                fontSize: 18, fontWeight: 700
                            }}>
                                {index + 1}
                            </div>

                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 16.5, fontWeight: 600, color: '#111' }}>
                                    {getDisplayTitle(item)}
                                </div>
                                <div style={{ fontSize: 13, color: '#666', marginTop: 3 }}>
                                    ID: {item.id} • {item.containerTypeDescription || 'Container'}
                                </div>
                            </div>

                            <div style={{ fontSize: 26, color: '#bbb' }}>⋮⋮</div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div style={{ padding: '20px 28px', borderTop: '1px solid #eee', display: 'flex', gap: 12 }}>
                    <button
                        onClick={onClose}
                        style={{ flex: 1, padding: '14px', borderRadius: 12, background: '#f5f5f5', border: '1px solid #ddd' }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={saveSequence}
                        disabled={saving}
                        style={{
                            flex: 1, padding: '14px', borderRadius: 12,
                            background: '#6C3FC5', color: '#fff', border: 'none',
                            fontWeight: 700
                        }}
                    >
                        {saving ? 'Saving...' : 'Save New Sequence'}
                    </button>
                </div>
            </div>
        </div>
    );
}